import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { GreenTick } from "@/components/common/Icons/Pricing/GreenTick";
import { Translate } from "@/components/common/Translate/Translate";
import { PricingGetStartedButton } from "components/modules/Pricing/PricingCard";
import { stripeProducts } from "components/modules/Pricing/PricingSection";

interface DropdownContent {
    [key: string]: {
        free: string[];
        pro: string[];
        ultra: string[];
    };
}

const dropdownContent: DropdownContent = {
    "file-size-per-image": {
        free: ["free-size-1", "free-size-2", "free-size-3", "free-size-4", "free-size-5"],
        pro: ["pro-size-1", "pro-size-2", "pro-size-3", "pro-size-4", "pro-size-5"],
        ultra: ["ultra-size-1", "ultra-size-2", "ultra-size-3", "ultra-size-4", "ultra-size-5"],
    },
    "batch-processing": {
        free: ["free-batch-1", "free-batch-2", "free-batch-3", "free-batch-4", "free-batch-5"],
        pro: ["pro-batch-1", "pro-batch-2", "pro-batch-3", "pro-batch-4", "pro-batch-5"],
        ultra: ["ultra-batch-1", "ultra-batch-2", "ultra-batch-3", "ultra-batch-4", "ultra-batch-5"],
    },
    "tool-access": {
        free: ["free-tool-1", "free-tool-2", "free-tool-3", "free-tool-4"],
        pro: ["pro-tool-1", "pro-tool-2", "pro-tool-3", "pro-tool-4"],
        ultra: ["ultra-tool-1", "ultra-tool-2", "ultra-tool-3", "ultra-tool-4"],
    },
};

const buttonTexts: Record<keyof DropdownContent, { free: string; pro: string; ultra: string }> = {
    "file-size-per-image": { free: "limited", pro: "larger-file-size-per-image", ultra: "highest-file-size-per-image" },
    "batch-processing": {
        free: "limited",
        pro: "larger-number-of-images",
        ultra: "highest-number-of-images-per-batch",
    },
    "tool-access": { free: "full-access", pro: "full-access", ultra: "full-access" },
};

export const PackageDetailsTable: React.FC = ({ stripeProductId }: { stripeProductId?: string }) => {
    const [openSection, setOpenSection] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"free" | "pro" | "ultra">("free");
    const [isMobileView, setIsMobileView] = useState(false);
    const toggleDropdown = (section: string) => {
        setOpenSection(openSection === section ? null : section);
    };
    const isDropdownOpen = (section: string) => openSection === section;
    useEffect(() => {
        const checkMobileView = () => {
            setIsMobileView(window.innerWidth <= 767);
        };
        // Check on initial load
        checkMobileView();
        // Add event listener for window resize
        window.addEventListener("resize", checkMobileView);
        // Cleanup
        return () => window.removeEventListener("resize", checkMobileView);
    }, []);

    function getStripeProductId(plan?: string): string | undefined {
        if (!plan) {
            return undefined;
        }
        return Object(stripeProducts)[plan];
    }

    return (
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-20 pb-16" id="features-section">
            <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-center mt-8 sm:mt-26 mb-6 sm:mb-14 text-gray-700 dark:text-gray-100">
                    <Translate keyName="compare-features" />
                </h2>
            </div>

            {/* Desktop View */}
            <div className={isMobileView ? "hidden" : "block overflow-x-auto"}>
                <table className="w-full border-collapse border border-gray-300 table-fixed">
                    <thead>
                        <tr>
                            <th className="border border-gray-300 p-6 text-left text-xl font-semibold h-24">
                                <Translate keyName="overview" />
                            </th>
                            <th className="border border-gray-300 p-6 text-center text-xl font-semibold h-24">
                                {"Free"}
                            </th>
                            <th className="border border-gray-300 p-6 text-center text-xl font-semibold h-24">
                                {"Pro"}
                            </th>
                            <th className="border border-gray-300 p-6 text-center text-xl font-semibold h-24">
                                {"Ultra"}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(dropdownContent).map((section) => (
                            <tr key={section} className="border border-gray-300">
                                <td className="border border-gray-300 p-6 text-lg h-32">
                                    <Translate keyName={section} />
                                </td>
                                {["free", "pro", "ultra"].map((plan) => (
                                    <td key={plan} className="border border-gray-300 p-5 h-32">
                                        <button
                                            onClick={() => toggleDropdown(section)}
                                            className="flex w-full text-lg hover:text-blue-700 font-medium justify-center text-center"
                                        >
                                            <Translate
                                                keyName={
                                                    buttonTexts[section as keyof typeof buttonTexts][
                                                        plan as "free" | "pro" | "ultra"
                                                    ]
                                                }
                                            />
                                            <ChevronDown
                                                className={`ml-1 mt-1 min-w-[20px] min-h-[20px] w-5 h-5 transition-transform ${
                                                    isDropdownOpen(section) ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        {isDropdownOpen(section) && (
                                            <div className="mt-4 space-y-3">
                                                {dropdownContent[section][
                                                    plan as keyof (typeof dropdownContent)[typeof section]
                                                ].map((item, index) => (
                                                    <div key={index} className="flex items-center text-base">
                                                        <GreenTick />
                                                        <span className="text-gray-700 dark:text-gray-400 break-words whitespace-normal max-w-full">
                                                            <Translate keyName={item} />
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {[
                            {
                                key: "daily-limit",
                                values: {
                                    free: "free-daily-limit",
                                    pro: "pro-daily-limit",
                                    ultra: "ultra-daily-limit",
                                },
                            },
                            {
                                key: "support",
                                values: { free: "free-support", pro: "free-support", ultra: "ultra-support" },
                            },
                            { key: "devices", values: { free: "included", pro: "included", ultra: "included" } },
                            {
                                key: "secure-connection",
                                values: { free: "included", pro: "included", ultra: "included" },
                            },
                            { key: "certified", values: { free: "included", pro: "included", ultra: "included" } },
                            { key: "ad-policy", values: { free: "ad-supported", pro: "no-ads", ultra: "no-ads" } },
                        ].map((row) => (
                            <tr key={row.key} className="border border-gray-300">
                                <td className="border border-gray-300 p-6 text-lg h-32">
                                    <Translate keyName={row.key} />
                                </td>
                                {["free", "pro", "ultra"].map((plan) => (
                                    <td
                                        key={plan}
                                        className="border border-gray-300 p-6 text-lg h-32 justify-center text-center"
                                    >
                                        <Translate keyName={row.values[plan as keyof typeof row.values]} />
                                    </td>
                                ))}
                            </tr>
                        ))}

                        <tr>
                            <td className="border-t border-l border-r border-gray-300"></td>
                            {["free", "pro", "ultra"].map((plan) => (
                                <td key={plan} className="border border-gray-300 p-6 text-lg h-32">
                                    <PricingGetStartedButton stripeProductId={getStripeProductId(plan)} />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <div className={isMobileView ? "block" : "hidden"}>
                <div className="flex mb-6 border rounded-lg overflow-hidden">
                    {(["free", "pro", "ultra"] as const).map((plan) => (
                        <button
                            key={plan}
                            onClick={() => setActiveTab(plan)}
                            className={`flex-1 p-3 text-center ${
                                activeTab === plan
                                    ? "bg-blue-500 text-white dark:bg-darkSurface-300"
                                    : "bg-gray-100 text-gray-700 dark:bg-gray-400"
                            }`}
                        >
                            <Translate keyName={plan} />
                        </button>
                    ))}
                </div>

                {Object.keys(dropdownContent).map((section) => (
                    <div key={section} className="border border-gray-200 rounded-md mb-4">
                        <div
                            className="p-4 flex justify-between items-center cursor-pointer"
                            onClick={() => toggleDropdown(section)}
                        >
                            <span className="text-lg font-semibold">
                                <Translate keyName={section} />
                            </span>
                            <ChevronDown
                                className={`w-5 h-5 transition-transform ${
                                    isDropdownOpen(section) ? "transform rotate-180" : ""
                                }`}
                            />
                        </div>
                        {isDropdownOpen(section) && (
                            <div className="p-4 bg-gray-100 dark:bg-darkSurface-200">
                                <div className="space-y-3">
                                    {dropdownContent[section][
                                        activeTab as keyof (typeof dropdownContent)[typeof section]
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center">
                                            <GreenTick />
                                            <span className="text-gray-700 dark:text-gray-400">
                                                <Translate keyName={item} />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {[
                    {
                        key: "daily-limit",
                        values: { free: "free-daily-limit", pro: "pro-daily-limit", ultra: "ultra-daily-limit" },
                    },
                    { key: "support", values: { free: "free-support", pro: "free-support", ultra: "ultra-support" } },
                    { key: "devices", values: { free: "included", pro: "included", ultra: "included" } },
                    { key: "secure-connection", values: { free: "included", pro: "included", ultra: "included" } },
                    { key: "certified", values: { free: "included", pro: "included", ultra: "included" } },
                    { key: "ad-policy", values: { free: "ad-supported", pro: "no-ads", ultra: "no-ads" } },
                ].map((row) => (
                    <div key={row.key} className="border border-gray-200 rounded-md p-4 mb-4">
                        <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">
                                <Translate keyName={row.key} />
                            </span>
                            <span className="text-gray-700 dark:text-gray-400">
                                <Translate keyName={row.values[activeTab]} />
                            </span>
                        </div>
                    </div>
                ))}
                <div className="mt-6">
                    <PricingGetStartedButton stripeProductId={getStripeProductId(activeTab)} />
                </div>
            </div>
        </div>
    );
};
